import "leaflet/dist/leaflet.css";
import { useMemo, useState } from "react";
import { MapContainer, TileLayer, CircleMarker, Popup, Tooltip } from "react-leaflet";
import { APIProvider, Map, AdvancedMarker, InfoWindow } from "@vis.gl/react-google-maps";
import { useNavigate } from "react-router-dom";
import { GMAPS_KEY, MAP_ID, GCircle, FitPoints, Dot } from "@/components/gmaps";

const JobPopup = ({ j, onOpen }) => (
  <div style={{ minWidth: 160 }}>
    <p style={{ fontWeight: 700, margin: 0 }}>{j.title}</p>
    <p style={{ margin: "4px 0", fontSize: 12, color: "#475569" }}>{j.pickup_suburb} → {j.dropoff_suburb}</p>
    <p style={{ margin: "4px 0", fontSize: 12 }}>{j.bid_count} bids{j.lowest_bid != null ? ` · from $${j.lowest_bid}` : ""}</p>
    <button onClick={onOpen} data-testid={`jobs-map-open-${j.id}`} style={{ background: "#E67E22", color: "#fff", border: 0, padding: "6px 10px", borderRadius: 6, fontWeight: 600, cursor: "pointer" }}>View & bid</button>
  </div>
);

function GoogleJobs({ pts, cov }) {
  const navigate = useNavigate();
  const [sel, setSel] = useState(null);
  const fit = pts.length ? pts.map((j) => ({ lat: j.pickup_lat, lng: j.pickup_lng })) : cov.map((c) => ({ lat: c.lat, lng: c.lon }));
  return (
    <APIProvider apiKey={GMAPS_KEY}>
      <Map mapId={MAP_ID} defaultCenter={{ lat: -28.5, lng: 134 }} defaultZoom={4} gestureHandling="cooperative"
        streetViewControl={false} mapTypeControl={false} fullscreenControl={false} style={{ width: "100%", height: "100%" }}>
        <FitPoints points={fit} />
        {cov.map((c, i) => <GCircle key={`c${i}`} center={{ lat: c.lat, lng: c.lon }} radius={4000} color="#2563EB" fillOpacity={0.12} weight={1} />)}
        {pts.map((j) => (
          <AdvancedMarker key={j.id} position={{ lat: j.pickup_lat, lng: j.pickup_lng }} title={j.title} onClick={() => setSel(j)}>
            <Dot color="#E67E22" size={18} />
          </AdvancedMarker>
        ))}
        {sel && (
          <InfoWindow position={{ lat: sel.pickup_lat, lng: sel.pickup_lng }} pixelOffset={[0, -14]} onCloseClick={() => setSel(null)}>
            <JobPopup j={sel} onOpen={() => navigate(`/jobs/${sel.id}`)} />
          </InfoWindow>
        )}
      </Map>
    </APIProvider>
  );
}

function LeafletJobs({ pts, cov }) {
  const navigate = useNavigate();
  const center = pts.length ? [pts[0].pickup_lat, pts[0].pickup_lng] : [-33.8688, 151.2093];
  return (
    <MapContainer center={center} zoom={pts.length ? 10 : 4} style={{ height: "100%", width: "100%" }} scrollWheelZoom={false}>
      <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
      {cov.map((c, i) => (
        <CircleMarker key={`c${i}`} center={[c.lat, c.lon]} radius={16} pathOptions={{ color: "#2563EB", fillColor: "#2563EB", fillOpacity: 0.12, weight: 1 }}>
          <Tooltip>{c.suburb}{c.state ? `, ${c.state}` : ""} (your area)</Tooltip>
        </CircleMarker>
      ))}
      {pts.map((j) => (
        <CircleMarker key={j.id} center={[j.pickup_lat, j.pickup_lng]} radius={9} pathOptions={{ color: "#CF6D17", fillColor: "#E67E22", fillOpacity: 0.9, weight: 2 }}>
          <Popup><JobPopup j={j} onOpen={() => navigate(`/jobs/${j.id}`)} /></Popup>
        </CircleMarker>
      ))}
    </MapContainer>
  );
}

export function JobsMap({ jobs = [], coverage = [] }) {
  const pts = useMemo(() => jobs.filter((j) => j.pickup_lat && j.pickup_lng), [jobs]);
  const cov = useMemo(() => coverage.filter((c) => c.lat && c.lon), [coverage]);
  return (
    <div className="rounded-2xl overflow-hidden border border-border" style={{ height: 460 }} data-testid="jobs-map" data-provider={GMAPS_KEY ? "google" : "osm"}>
      {GMAPS_KEY ? <GoogleJobs pts={pts} cov={cov} /> : <LeafletJobs pts={pts} cov={cov} />}
    </div>
  );
}
